import { createAsyncThunk } from '@reduxjs/toolkit'
import doAsync from '../../infrastructure/doAsync'
import { REGIONS } from '../../widgets/busyIndicator'
import { createFilterString } from '../../helpers/createFilterString'

export const fetchAccountUsers = createAsyncThunk(
	'fetch/accountUsers',
	async ({ useCaching = true, noBusySpinner } = {}, thunkArgs) => {
		return await doAsync({
			url: 'api/backOfficeUsers',
			useCaching,
			noBusySpinner,
			busyIndicatorName: REGIONS.FETCH_ACCOUNTS,
			errorMessage: 'Unable to load account users. Please try again later.',
			...thunkArgs,
		})
	}
)

export const fetchBackOfficeAccounts = createAsyncThunk(
	'fetch/backOfficeAccounts',
	async ({ filters, useCaching = true, noBusySpinner } = {}, thunkArgs) => {
		const fullQs = filters ? `?search=${filters}` : ''
		return await doAsync({
			url: `api/backOfficeUsers/accounts${fullQs}`,
			useCaching,
			noBusySpinner,
			errorMessage: 'Unable to load account users. Please try again later.',
			...thunkArgs,
		})
	}
)

export const deleteUser = createAsyncThunk(
	'delete/user',
	async ({ userId, data, useCaching = true, noBusySpinner } = {}, thunkArgs) =>
		await doAsync({
			url: `api/backOfficeUsers/${userId}`,
			httpMethod: 'delete',
			httpConfig: {
				body: JSON.stringify(data),
			},
			useCaching,
			noBusySpinner,
			busyIndicatorName: REGIONS.DELETE_ACCOUNT_USER,
			errorMessage: 'Unable to delete user. Please try again later.',
			...thunkArgs,
		})
)

// Self-service: user resets their own password (used by Profile page)
export const resetBackOfficeUserPassword = createAsyncThunk(
	'reset/backOfficeUserPassword',
	async (data, thunkArgs, { useCaching = true, noBusySpinner } = {}) =>
		await doAsync({
			url: `api/backOfficeUsers/${data.id}/password`,
			httpMethod: 'put',
			httpConfig: {
				body: JSON.stringify(data),
			},
			useCaching,
			noBusySpinner,
			busyIndicatorName: REGIONS.RESET_BACKOFFICE_USER_PASSWORD,
			errorMessage: 'Unable to update password. Please try again later.',
			...thunkArgs,
		})
)

// Admin action: sends a secure password reset email link (no direct password setting)
export const sendAccountPasswordResetLink = createAsyncThunk(
	'send/accountPasswordResetLink',
	async (data, thunkArgs, { noBusySpinner } = {}) =>
		await doAsync({
			url: `api/backOfficeUsers/${encodeURIComponent(data.id)}/send-password-reset`,
			httpMethod: 'post',
			useCaching: false,
			noBusySpinner,
			busyIndicatorName: REGIONS.RESET_BACKOFFICE_USER_PASSWORD,
			errorMessage: 'Unable to send password reset link. Please try again later.',
			...thunkArgs,
		})
)

export const updateAccountUser = createAsyncThunk(
	'update/accountUser',
	async (user, thunkArgs, { useCaching = true, noBusySpinner } = {}) =>
		await doAsync({
			url: `api/backOfficeUsers/${user.id}`,
			httpMethod: 'put',
			httpConfig: {
				body: JSON.stringify(user),
			},
			useCaching,
			noBusySpinner,
			busyIndicatorName: REGIONS.EDIT_ACCOUNT_USER,
			...thunkArgs,
		})
)

export const saveAccountUser = createAsyncThunk(
	'save/accountUser',
	async (data, thunkArgs, { useCaching = false, noBusySpinner } = {}) =>
		await doAsync({
			url: 'api/backOfficeUsers',
			httpMethod: 'post',
			httpConfig: {
				body: JSON.stringify(data),
			},
			useCaching,
			noBusySpinner,
			busyIndicatorName: REGIONS.ADD_ACCOUNT_USER,
			errorMessage: 'Unable to edit user. Please try again later.',
			...thunkArgs,
		})
)

export const fetchFFLDetails = createAsyncThunk(
	'fetch/fflDetailsByFFlNumber',
	async ({ filters, useCaching = true, noBusySpinner } = {}, thunkArgs) => {
		const filter = createFilterString(filters)
		const qs = filter ? `?${filter}` : ''

		return await doAsync({
			url: `api/accounts/fflDetails${qs}`,
			useCaching,
			noBusySpinner,
			busyIndicatorName: REGIONS.FETCH_ACCOUNTS,
			errorMessage: 'Unable to load account users. Please try again later.',
			...thunkArgs,
		})
	}
)

export const uploadUserManualFile = createAsyncThunk(
	'usermanual/upload',
	async (formData, thunkArgs, { useCaching, noBusySpinner } = {}) =>
		await doAsync({
			url: `api/usermanual`,
			httpMethod: 'post',
			httpConfig: { body: formData },
			isMultipartFormData: true,
			useCaching,
			noBusySpinner,
			showNoError: true,
			busyIndicatorName: REGIONS.UPLOAD_USER_MANUAL,
			errorMessage:
				'Unable to upload user manual file. Please try again later.',
			...thunkArgs,
		})
)

export const downloadUserManualFile = createAsyncThunk(
	'usermanual/download',
	async ({ useCaching } = {}, thunkArgs) =>
		await doAsync({
			url: `api/usermanual`,
			httpMethod: 'get',
			useCaching,
			busyIndicatorName: REGIONS.DOWNLOAD_USER_MANUAL,
			errorMessage: 'Unable to fetch user manual. Please try again later.',
			...thunkArgs,
		})
)

export const fetchAccountDetails = createAsyncThunk(
	'api/accounts/accountId',
	async (accountId, thunkArgs, { useCaching, noBusySpinner } = {}) =>
		await doAsync({
			url: `api/accounts/${accountId}`,
			useCaching,
			noBusySpinner,
			errorMessage: 'Unable to load account details . Please try again later.',
			...thunkArgs,
		})
)

export const updateAccount = createAsyncThunk(
	'api/accounts/accountId',
	async (
		{ accountId, account },
		thunkArgs,
		{ useCaching, noBusySpinner } = {}
	) =>
		await doAsync({
			url: `api/accounts/${accountId}`,
			httpMethod: 'put',
			httpConfig: {
				body: JSON.stringify(account),
			},
			useCaching,
			noBusySpinner,
			busyIndicatorName: REGIONS.UPDATE_ACCOUNT_DETAILS,
			errorMessage: 'Unable to update account details. Please try again later.',
			...thunkArgs,
		})
)

export const deleteAccount = createAsyncThunk(
	'api/accounts/accounId',
	async (accountId, thunkArgs, { useCaching, noBusySpinner } = {}) =>
		await doAsync({
			url: `api/accounts/${accountId}`,
			httpMethod: 'delete',
			useCaching,
			noBusySpinner,
			busyIndicatorName: REGIONS.DELETE_DUPLICATE_ACCOUNT,
			errorMessage: 'Unable to delete account. Please try again later.',
			...thunkArgs,
		})
)
